import { Outlet, Link, useLocation } from "react-router-dom"
import { FormEvent, useMemo, useState } from "react"
import {
  Bell,
  ClipboardList,
  LayoutDashboard,
  Menu,
  Search,
  Settings,
  User,
  Users,
  UsersRound,
  Wallet,
  X,
  Sparkles,
} from "lucide-react"

import { cn } from "@/lib/utils"
import { getApiBaseUrl, readApiJson } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Card, CardHeader, CardTitle } from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

const APP_NAV = [
  { label: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
  { label: "Client Management", href: "/client-management", icon: Users },
  { label: "Payroll Settings", href: "/payroll-settings", icon: Wallet },
  { label: "Admin Settings", href: "/admin-settings", icon: Settings },
  { label: "User Management", href: "/user-management", icon: UsersRound },
] as const

const OWNER_AUTH_STORAGE_KEY = "nestor-owner-console-authenticated"
const OWNER_AUTH_USER_KEY = "nestor-owner-console-user"

function useActivePath() {
  const location = useLocation()
  return location.pathname
}

function AppSidebar() {
  const pathname = useActivePath()

  return (
    <aside
      className={cn(
        "hidden md:flex w-64 flex-col min-h-screen",
        "bg-gradient-to-br from-slate-600 via-slate-600 to-slate-700",
        "border-r border-slate-500/50 shadow-xl"
      )}
    >
      <div className="flex items-center h-20 px-4 border-b border-slate-500/30 bg-slate-600/90 backdrop-blur-sm">
        <Link to="/" className="flex items-center">
          <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 via-blue-500 to-cyan-400 flex items-center justify-center shadow-lg">
            <span className="text-white font-bold text-xl">N</span>
            <Sparkles className="absolute -top-1 -right-1 h-4 w-4 text-amber-500 bg-white rounded-full p-[2px] shadow-sm ring-1 ring-slate-200" />
          </div>
          <div className="ml-3 overflow-hidden">
            <h1 className="font-bold text-xl truncate text-white">Nestor</h1>
            <p className="text-xs text-slate-200 truncate font-medium">Management Portal</p>
          </div>
        </Link>
      </div>

      <nav className="flex-1 px-4 py-2 space-y-1 overflow-y-auto">
        {APP_NAV.map((item) => {
          const active = pathname === item.href || pathname.startsWith(item.href + "/")
          const Icon = item.icon
          return (
            <Button
              key={item.href}
              variant={active ? "default" : "ghost"}
              className={cn(
                "w-full justify-start text-left font-normal text-sm h-10",
                active
                  ? "bg-white text-slate-700 border border-white/20 shadow-lg hover:bg-white/95"
                  : "text-slate-100 hover:text-white hover:bg-white/10",
                "transition-all duration-200"
              )}
              asChild
            >
              <Link to={item.href}>
                <span className="mr-3">
                  <Icon className="w-5 h-5" />
                </span>
                <span className="text-xs sm:text-sm truncate">{item.label}</span>
              </Link>
            </Button>
          )
        })}
      </nav>

      <div className="px-4 py-2 border-t border-slate-500/30 bg-slate-600/90">
        <div className="text-xs text-center text-slate-300 p-2">Nestor v1.0.0</div>
      </div>
    </aside>
  )
}

function AppHeader({
  onToggleSidebar,
  onLogout,
}: {
  onToggleSidebar: () => void
  onLogout: () => void
}) {
  const pathname = useActivePath()

  const title = useMemo(() => {
    return (
      APP_NAV.find((x) => pathname === x.href || pathname.startsWith(x.href + "/"))?.label ??
      "Admin Console"
    )
  }, [pathname])

  // Placeholder counts (wire to real data later)
  const taskCount = 0
  const unreadCount = 0

  return (
    <header className="sticky top-0 z-40 w-full border-b bg-white">
      <div className="flex h-20 items-center gap-2 sm:gap-3 px-3 sm:px-4 md:px-6">
        {/* Left - Menu */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleSidebar}
          className="h-10 w-10 flex-shrink-0"
          aria-label="Toggle Menu"
        >
          <Menu className="h-5 w-5" />
        </Button>

        {/* Title (mobile) */}
        <div className="md:hidden min-w-0">
          <div className="text-sm font-semibold leading-tight truncate">{title}</div>
          <div className="text-xs text-muted-foreground leading-tight truncate">
            Internal console
          </div>
        </div>

        {/* Middle - Search (desktop) */}
        <div className="hidden md:block flex-shrink-0 md:w-[280px] lg:w-[400px] xl:w-[500px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search clients, payroll settings..."
              aria-label="Search"
            />
          </div>
        </div>

        {/* Spacer */}
        <div className="flex-1 min-w-[20px]" />

        {/* Right - Actions */}
        <div className="flex items-center gap-3">
          {/* Tasks */}
          <Button variant="ghost" size="icon" className="relative h-10 w-10 flex-shrink-0">
            <ClipboardList className="h-5 w-5" />
            {taskCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white pointer-events-none">
                {taskCount}
              </span>
            )}
          </Button>

          {/* Notifications */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative h-10 w-10 flex-shrink-0">
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white pointer-events-none">
                    {unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[360px] max-w-[calc(100vw-2rem)]">
              <DropdownMenuLabel className="font-semibold">
                Notifications
                {unreadCount > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {unreadCount} new
                  </Badge>
                )}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <div className="p-4 text-sm text-muted-foreground">
                No notifications yet.
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10 rounded-full border bg-background">
                <User className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-semibold">Admin</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="gap-2">
                <User className="h-4 w-4" /> Profile
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2">
                <Settings className="h-4 w-4" /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2" onClick={onLogout}>
                <X className="h-4 w-4" /> Logout
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <a href="/" rel="noreferrer" className="cursor-pointer">
                  Back to Website
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  )
}

function OwnerLogin({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setLoading(true)
    setError("")

    try {
      const response = await fetch(`${getApiBaseUrl()}/owner/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: username.trim(),
          password,
        }),
      })

      const json = await readApiJson(response, "Invalid username or password.")

      localStorage.setItem(OWNER_AUTH_STORAGE_KEY, "true")
      localStorage.setItem(OWNER_AUTH_USER_KEY, JSON.stringify(json?.data || { username: username.trim() }))
      onAuthenticated()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid username or password.")
      window.setTimeout(() => setError(""), 5000)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-700 via-slate-800 to-slate-950 flex items-center justify-center px-4">
      <Card className="w-full max-w-md border-white/10 shadow-2xl">
        <CardHeader>
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 via-blue-500 to-cyan-400 text-xl font-bold text-white">
            N
          </div>
          <CardTitle className="text-center text-2xl">Owner Console Login</CardTitle>
          <p className="text-center text-sm text-muted-foreground">
            Sign in to access the SaaS owner pages.
          </p>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <div className="px-6 pb-6 space-y-4">
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <div className="text-sm font-medium">User Name</div>
              <Input
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                placeholder="Admin"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium">Password</div>
              <Input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Enter password"
              />
            </div>
            <Button type="submit" className="w-full">
              {loading ? "Logging in..." : "Login"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}

export function AppShellLayout() {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [authenticated, setAuthenticated] = useState(() => {
    try {
      return localStorage.getItem(OWNER_AUTH_STORAGE_KEY) === "true"
    } catch {
      return false
    }
  })

  function handleLogout() {
    localStorage.removeItem(OWNER_AUTH_STORAGE_KEY)
    localStorage.removeItem(OWNER_AUTH_USER_KEY)
    setAuthenticated(false)
    setMobileSidebarOpen(false)
  }

  if (!authenticated) {
    return <OwnerLogin onAuthenticated={() => setAuthenticated(true)} />
  }

  return (
    <div className="min-h-screen bg-background text-foreground hrms-ui font-hrms">
      <div className="flex min-h-screen">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <AppHeader
            onToggleSidebar={() => setMobileSidebarOpen((s) => !s)}
            onLogout={handleLogout}
          />

          {/* Mobile sidebar */}
          {mobileSidebarOpen && (
            <div className="md:hidden fixed inset-0 z-50">
              <div
                className="absolute inset-0 bg-black/40"
                onClick={() => setMobileSidebarOpen(false)}
              />
              <div className="absolute left-0 top-0 h-full w-80 bg-gradient-to-br from-slate-600 via-slate-600 to-slate-700 border-r border-slate-500/50 shadow-xl">
                <div className="h-20 px-3 flex items-center justify-between border-b border-slate-500/30 bg-slate-600/90 backdrop-blur-sm">
                  <div className="flex items-center gap-2">
                    <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 via-blue-500 to-cyan-400 flex items-center justify-center shadow-lg">
                      <span className="text-white font-bold text-xl">N</span>
                      <Sparkles className="absolute -top-1 -right-1 h-4 w-4 text-amber-500 bg-white rounded-full p-[2px] shadow-sm ring-1 ring-slate-200" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold leading-tight truncate">
                        Nestor
                      </div>
                      <div className="text-xs text-slate-200 leading-tight truncate font-medium">Management Portal</div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 text-slate-100 hover:bg-white/10 hover:text-white"
                    onClick={() => setMobileSidebarOpen(false)}
                    aria-label="Close menu"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>

                <div className="p-3 space-y-1">
                  {APP_NAV.map((item) => {
                    const Icon = item.icon
                    return (
                      <Button
                        key={item.href}
                        variant="ghost"
                        className="w-full justify-start gap-2 text-slate-100 hover:bg-white/10 hover:text-white h-10"
                        asChild
                      >
                        <Link to={item.href} onClick={() => setMobileSidebarOpen(false)}>
                          <Icon className="w-5 h-5" />
                          <span className="text-xs sm:text-sm truncate">{item.label}</span>
                        </Link>
                      </Button>
                    )
                  })}
                </div>

                <div className="mt-auto px-4 py-2 border-t border-slate-500/30 bg-slate-600/90">
                  <div className="text-xs text-center text-slate-300 p-2">Nestor v1.0.0</div>
                </div>
              </div>
            </div>
          )}

          <main className="flex-1 p-4 md:p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  )
}

